import React, { useCallback, useEffect, useState, useRef } from 'react';
import { BottomSection, ButtonSection, Icon, Label, Logo, MainCard, Title, TopRightSection, TopSection } from './style';
import { Images } from '../../assets/images/images';
import { useSelector } from 'react-redux';
import { RootReducerInterface } from '../../interfaces/RootReducerInterface';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, DEPLOYMENT_STATUS_LABEL } from '../../global/constants';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { createBranchDeployment, getVercelDeployment } from '../../services/vercelService';
import { dispatchTaskError, dispatchTaskSuccess } from '../../services/taskServices';
import { VercelDeployments } from '../../mobx/interfaces/project';
import { isEmpty } from '../../helpers/common';
import ButtonV2 from '../button/buttonV2';

const VercelCard = () => {
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const { settings: settingsSelector } = stateSelector;
  const { themeMode } = settingsSelector;
  //Mobx store variables
  const { taskStore, projectStore } = rootStore;
  const { currentTask } = taskStore;
  const { currentProject } = projectStore;
  //Other variables
  const dispatch = useDispatch();

  // Add state for polling and timer
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  //Handle preview link visit click
  const onClickPreviewLink = useCallback((url: string) => {
    window.open(`https://${url}`, '_blank');
  }, []);

  // Function to check if any deployment is running
  const hasRunningDeployment = useCallback(() => {
    if (!currentTask?.vercelDeployments || currentTask?.vercelDeployments.length === 0) return false;
    return currentTask?.vercelDeployments?.some(
      (deploy) =>
        deploy.state === DEPLOYMENT_STATUS_LABEL.BUILDING ||
        deploy.state === DEPLOYMENT_STATUS_LABEL.INITIALIZING ||
        deploy.state === DEPLOYMENT_STATUS_LABEL.QUEUED
    );
  }, [currentTask?.vercelDeployments]);

  // Function to update elapsed time
  const updateElapsedTime = useCallback((deploy: any) => {
    const startTime = moment(deploy.createdAt);
    const now = moment();
    const duration = moment.duration(now.diff(startTime));
    const minutes = String(Math.floor(duration.asMinutes())).padStart(2, '0');
    const seconds = String(duration.seconds()).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, []);

  const loadData = useCallback(async () => {
    if (currentProject?.id && currentTask?.id) {
      await dispatch(getVercelDeployment(currentProject.id, currentTask.id));
    }
  }, [currentProject?.id, currentTask?.id, dispatch]);

  // Polling effect for running deployments
  useEffect(() => {
    const isRunning = hasRunningDeployment();

    if (isRunning) {
      // Start polling every 10 seconds
      intervalRef.current = setInterval(() => {
        loadData();
      }, 10000);

      // Update elapsed time every second
      const timeInterval = setInterval(() => {
        const runningDeploy = currentTask?.vercelDeployments?.find(
          (deploy) =>
            deploy.state === DEPLOYMENT_STATUS_LABEL.BUILDING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.INITIALIZING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.QUEUED
        );

        if (runningDeploy) {
          setElapsedTime(updateElapsedTime(runningDeploy));
        }
      }, 1000);

      // Cleanup function
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        clearInterval(timeInterval);
      };
    } else {
      // Clear intervals if no running deployment
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setElapsedTime('');
    }
  }, [hasRunningDeployment, loadData, currentTask?.vercelDeployments, updateElapsedTime]);

  const onClickDeploy = useCallback(
    async (branchName: string) => {
      try {
        const payload = {
          branchName,
          projectId: currentProject?.id
        };
        const response = await dispatch(createBranchDeployment(payload));
        if (response?.data?.id) {
          dispatchTaskSuccess('Deployment added in queue successfully');
          dispatch(getVercelDeployment(currentProject?.id, currentTask?.id));
        } else {
          dispatchTaskError('Something went wrong! Deployment not done');
        }
      } catch (error) {
        console.log('Error', error);
      }
    },
    [currentProject?.id, currentTask?.id, dispatch]
  );

  // Renders the main Vercel deployment card UI for a given deployment state
  const vercelMainCard = useCallback(
    (isEmpty?: boolean, deploy?: VercelDeployments, isRunning?: boolean) => {
      const LatestPrBranch = isEmpty
        ? currentTask?.pullRequests[currentTask?.pullRequests?.length - 1].branchName || ''
        : '';
      const branchName = !isEmpty && deploy ? deploy?.branchName : LatestPrBranch;
      return (
        <>
          <MainCard key={deploy?.id}>
            <TopSection>
              <TopRightSection>
                <Logo>
                  {themeMode?.theme === 'dark' ? (
                    <img src={Images.vercel_dark_logo} alt='vercel-logo' width={20} height={20} />
                  ) : (
                    <img src={Images.vercel_logo} alt='vercel-logo' width={20} height={20} />
                  )}
                </Logo>
                <Label>Vercel</Label>
              </TopRightSection>
              <ButtonSection>
                {!isEmpty && deploy?.url && (
                  <Icon onClick={() => onClickPreviewLink(deploy?.url)}>
                    <SVGIcon name='arrow-ne' width='16' height='16' viewBox='0 0 18 18' fill='var(--text-primary)' />
                  </Icon>
                )}
                <ButtonV2
                  title={isEmpty ? 'Deploy' : 'Redeploy'}
                  variant={BUTTON_TYPE.SECONDARY}
                  size='small'
                  disabled={isRunning}
                  onClick={() => onClickDeploy(branchName)}
                />
              </ButtonSection>
            </TopSection>
            <BottomSection>
              {!isEmpty && deploy?.ready && <Title>Last Deployed: {moment(deploy?.ready).fromNow()}</Title>}
              {(deploy?.aheadCommits || 0) > 0 && <Title>⚠️ GitHub ahead by {deploy?.aheadCommits} commits</Title>}
              {deploy?.state === DEPLOYMENT_STATUS_LABEL.CANCELED && <Title>Deployment cancelled</Title>}
              {deploy?.state === DEPLOYMENT_STATUS_LABEL.ERROR && <Title>🧾️ Last deployment was failed</Title>}

              {isRunning && <Title>Deployment is running... {elapsedTime}</Title>}
              {isEmpty && <Title>No Deployment yet</Title>}
            </BottomSection>
          </MainCard>
        </>
      );
    },
    [currentTask?.pullRequests, elapsedTime, onClickDeploy, onClickPreviewLink, themeMode?.theme]
  );
  return (
    <>
      {/* shoe empty state */}
      {currentTask?.pullRequests &&
        currentTask?.pullRequests?.length > 0 &&
        (!currentTask?.vercelDeployments || isEmpty(currentTask?.vercelDeployments)) &&
        vercelMainCard(true)}

      {/* Map all the vercel deployments */}
      {currentTask?.vercelDeployments &&
        currentTask?.vercelDeployments.length > 0 &&
        currentTask?.vercelDeployments.map((deploy) => {
          const isRunning =
            deploy.state === DEPLOYMENT_STATUS_LABEL.BUILDING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.INITIALIZING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.QUEUED;
          return vercelMainCard(false, deploy, isRunning);
        })}
    </>
  );
};

export default observer(VercelCard);
